/**
 * Single source of truth for the static answer landing pages emitted by
 * scripts/generate-answer-pages.ts.
 *
 * Consumed by:
 *   - generate-answer-pages.ts  (page specs assert their slug is registered)
 *   - generate-seo-pages.ts     (site header/footer navigation)
 *   - generate-sitemap.ts       (sitemap entries)
 *   - verify-seo.ts             (asserts every registered page was emitted)
 *   - generate-llms.ts          (llms.txt answer-page entries)
 *   - src/components/Layout/HomeLayout.tsx (SPA footer "Guides" links;
 *     bundled into the browser app, so keep this file free of imports)
 *
 * When adding a page here, also add its PageSpec in generate-answer-pages.ts —
 * the two cross-validate at build time and throw on drift.
 *
 * No _redirects entry is needed (pages are flat .html files served by
 * Cloudflare Pages at the no-trailing-slash URL), and nginx.conf matches all
 * of them with one `location ~ ^/discord-[a-z0-9-]+$` block. public/sitemap.xml
 * is generated from this registry too, so it can no longer drift by hand.
 */

/**
 * Footer grouping. Each answer page belongs to exactly one cluster, and the
 * footer renders one column per cluster — every static page therefore links
 * to every other one, which is how a newly published page inherits crawl
 * priority from the pages that already have it.
 */
export type AnswerPageGroup = "conditions" | "sync" | "help";

export const GROUP_HEADINGS: Record<AnswerPageGroup, string> = {
  conditions: "Role conditions",
  sync: "Sync & verification",
  help: "Compare & troubleshoot",
};

export interface AnswerPageMeta {
  /** URL slug — must start with "discord-" (nginx + _headers rely on it). */
  slug: string;
  /** Short label used in static-page footer navigation. */
  navLabel: string;
  /**
   * Descriptive anchor text for in-body internal links. Nav labels are short
   * because columns are narrow; body links should say what is on the other
   * side, because that text is the strongest signal about what the target
   * page is for.
   */
  anchorText: string;
  /** Which footer column the page appears under. */
  group: AnswerPageGroup;
  /** Sitemap priority. */
  priority: number;
  /** Sitemap change frequency. */
  changefreq: string;
  /** Promote to the (space-constrained) primary header nav. */
  inHeaderNav?: boolean;
}

export const ANSWER_PAGES: AnswerPageMeta[] = [
  /* ── Pillar ─────────────────────────────────────────────────────────── */
  {
    slug: "discord-role-bot",
    navLabel: "Discord role bot",
    anchorText: "What a Discord role bot does",
    group: "conditions",
    priority: 0.95,
    changefreq: "weekly",
    inHeaderNav: true,
  },

  /* ── Role-condition cluster ─────────────────────────────────────────── */
  {
    slug: "discord-conditional-roles",
    navLabel: "Role Conditions",
    anchorText: "Give a Discord role based on another role",
    group: "conditions",
    priority: 0.95,
    changefreq: "weekly",
    inHeaderNav: true,
  },
  {
    slug: "discord-autorole-bot",
    navLabel: "Autorole bot",
    anchorText: "A Discord autorole bot that repairs itself",
    group: "conditions",
    priority: 0.9,
    changefreq: "weekly",
  },
  {
    slug: "discord-remove-role-when-role-added",
    navLabel: "Exclusive Roles",
    anchorText: "Remove a Discord role when another one is added",
    group: "conditions",
    priority: 0.9,
    changefreq: "weekly",
  },

  /* ── Sync / verification cluster ────────────────────────────────────── */
  {
    slug: "discord-cross-server-role-sync",
    navLabel: "Cross-Server Sync",
    anchorText: "Sync Discord roles across multiple servers",
    group: "sync",
    priority: 0.95,
    changefreq: "weekly",
    inHeaderNav: true,
  },
  {
    slug: "discord-verification-bot",
    navLabel: "Verification",
    anchorText: "Verify member accounts before granting a role",
    group: "sync",
    priority: 0.9,
    changefreq: "weekly",
  },
  {
    slug: "discord-server-booster-role-bot",
    navLabel: "Booster Roles",
    anchorText: "Automate Server Booster perk roles",
    group: "sync",
    priority: 0.9,
    changefreq: "weekly",
  },

  /* ── Comparison / troubleshooting cluster ───────────────────────────── */
  {
    slug: "discord-role-bot-alternative",
    navLabel: "Bot comparison",
    anchorText: "Which Discord role bot to use",
    group: "help",
    priority: 0.85,
    changefreq: "monthly",
  },
  {
    slug: "discord-bot-not-assigning-roles",
    navLabel: "Roles not working?",
    anchorText: "Fix a Discord bot that is not assigning roles",
    group: "help",
    priority: 0.85,
    changefreq: "monthly",
  },
];

export const headerNavPages = (): AnswerPageMeta[] =>
  ANSWER_PAGES.filter((p) => p.inHeaderNav);

export const pagesInGroup = (group: AnswerPageGroup): AnswerPageMeta[] =>
  ANSWER_PAGES.filter((p) => p.group === group);
